/**
 * Phase 5: Integration
 *
 * The celebration settles into permanent UI elements.
 * Triggers cursor sparkle easter egg.
 */

import { useEffect, useState, useRef } from 'react';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import { AutoAwesome } from '@mui/icons-material';
import CursorSparkle from '../effects/CursorSparkle';
import { GENESIS_COLORS } from '../types';

interface PhaseIntegrationProps {
  /** Whether this phase is active */
  active: boolean;
  /** Callback when phase completes */
  onComplete: () => void;
  /** Duration in ms */
  duration?: number;
}

const BURST_COLORS = [GENESIS_COLORS.primary, GENESIS_COLORS.secondary, GENESIS_COLORS.accent, '#FFD700'];

const PhaseIntegration = ({ active, onComplete, duration = 2000 }: PhaseIntegrationProps) => {
  const [showSparkle, setShowSparkle] = useState(false);
  const [showFinalBurst, setShowFinalBurst] = useState(false);
  const hasStartedRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!active || hasStartedRef.current) return;
    hasStartedRef.current = true;

    // Show final sparkle burst
    setShowFinalBurst(true);

    // Enable cursor sparkle easter egg
    const t1 = setTimeout(() => {
      setShowSparkle(true);
    }, 500);

    // Complete the phase
    const t2 = setTimeout(() => {
      setShowFinalBurst(false);
      onComplete();
    }, duration);

    timersRef.current.push(t1, t2);

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [active, duration, onComplete]);

  // Reset when deactivated
  useEffect(() => {
    if (!active) {
      hasStartedRef.current = false;
      setShowFinalBurst(false);
    }
  }, [active]);

  if (!active && !showSparkle) return null;

  return (
    <>
      {/* Final burst animation */}
      {showFinalBurst && (
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            pointerEvents: 'none',
          }}
        >
          {/* Fading backdrop */}
          <Box
            component={motion.div}
            initial={{ opacity: 0.6 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.5)',
            }}
          />

          {/* Center sparkle burst */}
          <Box
            component={motion.div}
            initial={{ scale: 1, opacity: 1 }}
            animate={{ scale: 0, opacity: 0 }}
            transition={{ duration: 1, ease: 'easeIn' }}
          >
            <AutoAwesome
              component={motion.svg}
              animate={{ rotate: 360 }}
              transition={{ duration: 1, ease: 'linear' }}
              sx={{
                fontSize: 80,
                color: GENESIS_COLORS.accent,
                filter: `drop-shadow(0 0 30px ${GENESIS_COLORS.accent})`,
              }}
            />
          </Box>

          {/* Radial sparkle burst */}
          {Array.from({ length: 12 }).map((_, i) => (
            <Box
              key={i}
              component={motion.div}
              initial={{ scale: 1, opacity: 1 }}
              animate={{
                x: Math.cos((i / 12) * Math.PI * 2) * 200,
                y: Math.sin((i / 12) * Math.PI * 2) * 200,
                scale: 0,
                opacity: 0,
              }}
              transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
              sx={{
                position: 'absolute',
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: BURST_COLORS[i % BURST_COLORS.length],
                boxShadow: '0 0 15px currentColor',
              }}
            />
          ))}
        </Box>
      )}

      {/* Cursor sparkle easter egg - persists after phase */}
      <CursorSparkle active={showSparkle} duration={5000} />
    </>
  );
};

export default PhaseIntegration;
